import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Modal,
  Select,
  Table,
  Tag,
  message,
} from 'antd';
import {
  BellOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import PersianDatePicker from '../../components/PersianDatePicker';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { getLast30DaysJalaliApiRange } from '../../utils/dateUtils';
import { financeSettingsApi } from '../../utils/api';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useListReturnState } from '../../hooks/useListReturnState';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { parseJalaliDateRangeValue } from '../../utils/filterDateUtils';
import { PANEL_DANGER_OK_BUTTON_PROPS } from '../../constants/ui';
import { formatClientNameWithCompany } from '../../utils/clientDisplayUtils';
import ClientSelect from '../../components/common/ClientSelect';

const { Option } = Select;

const statusOptions = [
  { value: 'all', label: 'همه' },
  { value: 'unpaid', label: 'پرداخت نشده' },
  { value: 'paid', label: 'پرداخت شده' },
  { value: 'partial', label: 'پرداخت ناقص' },
  { value: 'canceled', label: 'لغو شده' },
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'pending-confirmation', label: 'در انتظار تایید' },
];

const statusTag = {
  unpaid: { color: 'red', text: 'پرداخت نشده' },
  paid: { color: 'green', text: 'پرداخت شده' },
  partial: { color: 'orange', text: 'پرداخت ناقص' },
  canceled: { color: 'default', text: 'لغو شده' },
  cancelled: { color: 'default', text: 'لغو شده' },
  draft: { color: 'blue', text: 'پیش‌نویس' },
  'pending-confirmation': { color: 'processing', text: 'در انتظار تایید' },
};

const INVOICE_STATUS_CHANGE_OPTIONS = [
  { value: 'paid', label: 'پرداخت شده', color: 'green' },
  { value: 'unpaid', label: 'پرداخت نشده', color: 'red' },
  { value: 'partial', label: 'پرداخت ناقص', color: 'orange' },
  { value: 'canceled', label: 'لغو شده', color: 'default' },
  { value: 'draft', label: 'پیش‌نویس', color: 'blue' },
  { value: 'pending-confirmation', label: 'در انتظار تایید', color: 'processing' },
];

const isCanceledInvoice = (record) => ['canceled', 'cancelled'].includes(String(record?.status || '').toLowerCase());

const Invoices = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDeleteRecord } = useResourcePermissions('invoices');
  const defaultRange = useMemo(() => getLast30DaysJalaliApiRange(), []);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0, last_page: 1 });
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    client_id: 'all',
    project_id: 'all',
    page: 1,
    per_page: 10,
  });
  const [, setFilterOptions] = useState({ clients: [], projects: [] });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const captureCurrentListState = useCallback(
    () => ({ filters }),
    [filters],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
  });

  const getActionKey = (id, action) => `${id}-${action}`;
  const isActionLoading = (id, action) => actionLoadingKey === getActionKey(id, action);
  const dateRangeValue = filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null;

  const handleDateRangeChange = (value) => {
    const { startDate, endDate } = parseJalaliDateRangeValue(value);
    setFilters((prev) => ({ ...prev, startDate, endDate, page: 1 }));
  };

  /** همیشه با فیلترهای فعلی (بدون ریست) لیست را می‌گیرد */
  const fetchInvoices = useCallback(async (activeFilters = filtersRef.current) => {
    setLoading(true);
    try {
      const res = await financeSettingsApi.getInvoices({
        ...activeFilters,
        client_id: activeFilters.client_id === 'all' ? undefined : activeFilters.client_id,
        project_id: activeFilters.project_id === 'all' ? undefined : activeFilters.project_id,
      });
      setInvoices(res?.invoices || []);
      setPagination(res?.pagination || {
        current_page: activeFilters.page || 1,
        per_page: activeFilters.per_page || 10,
        total: 0,
        last_page: 1,
      });
      setFilterOptions({
        projects: res?.filters?.projects || [],
      });
    } catch (error) {
      message.error('خطا در دریافت فاکتورها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices(filters);
  }, [filters, fetchInvoices]);

  const changeInvoiceStatus = async (record, newStatus) => {
    if (String(record.status) === String(newStatus)) return;
    setStatusLoadingId(record.id);
    try {
      await financeSettingsApi.changeInvoiceStatus({
        invoiceId: record.id,
        status: newStatus,
      });
      const label = statusTag[newStatus]?.text || newStatus;
      message.success(`وضعیت به «${label}» تغییر کرد`);
      await fetchInvoices(filtersRef.current);
    } catch (error) {
      console.error('خطا در تغییر وضعیت فاکتور:', error);
      message.error(error?.message || 'خطا در تغییر وضعیت فاکتور');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleSendInvoice = async (id) => {
    setActionLoadingKey(getActionKey(id, 'send'));
    try {
      await financeSettingsApi.sendInvoice(id);
      message.success('فاکتور ارسال شد');
      await fetchInvoices(filtersRef.current);
    } catch (error) {
      message.error('خطا در ارسال فاکتور');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleReminder = async (id) => {
    setActionLoadingKey(getActionKey(id, 'remind'));
    try {
      await financeSettingsApi.sendInvoicePaymentReminder(id);
      message.success('یادآوری پرداخت ارسال شد');
    } catch (error) {
      message.error('خطا در ارسال یادآوری');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleCancelInvoice = async (id) => {
    setActionLoadingKey(getActionKey(id, 'cancel'));
    try {
      await financeSettingsApi.cancelInvoice(id);
      message.success('فاکتور لغو شد');
      await fetchInvoices(filtersRef.current);
    } catch (error) {
      message.error('خطا در لغو فاکتور');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDownloadInvoice = async (id) => {
    setActionLoadingKey(getActionKey(id, 'download'));
    try {
      await financeSettingsApi.downloadInvoice(id);
      message.success('دانلود فاکتور آغاز شد');
    } catch (error) {
      message.error('خطا در دانلود فاکتور');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDeleteInvoice = async (id) => {
    setActionLoadingKey(getActionKey(id, 'delete'));
    try {
      await financeSettingsApi.deleteInvoice(id);
      message.success('فاکتور حذف شد');
      await fetchInvoices(filtersRef.current);
    } catch (error) {
      message.error('خطا در حذف فاکتور');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const confirmDeleteInvoice = (record) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف فاکتور؟</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این فاکتور اطمینان دارید؟</span>,
      okText: 'بله',
      cancelText: 'خیر',
      okType: 'primary',
      okButtonProps: PANEL_DANGER_OK_BUTTON_PROPS,
      onOk: () => handleDeleteInvoice(record.id),
    });
  };

  const columns = [
    {
      title: 'شماره فاکتور',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v || '-'}</span>,
    },
    {
      title: 'مشتری',
      key: 'client',
      render: (_, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatClientNameWithCompany(r.client, r) || '-'}
        </span>
      ),
    },
    {
      title: 'تاریخ صدور',
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v ? new Date(v).toLocaleDateString('fa-IR') : '-'}</span>,
    },
    {
      title: 'مبلغ کل',
      dataIndex: 'total',
      key: 'total',
      render: (v, r) => <span style={{ fontFamily: 'CustomFont' }}>{formatMoney(v, r.currency_symbol)}</span>,
    },
    {
      title: 'مانده',
      dataIndex: 'due_amount',
      key: 'due_amount',
      render: (v, r) => <span style={{ fontFamily: 'CustomFont' }}>{formatMoney(v, r.currency_symbol)}</span>,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (v, record) => {
        const current = String(v || '').toLowerCase() === 'cancelled' ? 'canceled' : v;
        const s = statusTag[current] || statusTag[v] || { color: 'default', text: v || '-' };
        const canChange = canEditRecord(record);
        if (!canChange) {
          return <Tag color={s.color} style={{ fontFamily: 'CustomFont' }}>{s.text}</Tag>;
        }
        const items = INVOICE_STATUS_CHANGE_OPTIONS.map((opt) => ({
          key: opt.value,
          label: (
            <span style={{ fontFamily: 'CustomFont' }}>
              <Tag color={opt.color} style={{ margin: 0, fontFamily: 'CustomFont' }}>{opt.label}</Tag>
              {opt.value === current ? <span className="text-xs text-gray-400 mr-2">✓</span> : null}
            </span>
          ),
        }));
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => changeInvoiceStatus(record, key) }}
            trigger={['click']}
          >
            <Button
              type="text"
              size="small"
              loading={statusLoadingId === record.id}
              style={{ fontFamily: 'CustomFont' }}
            >
              <Tag color={s.color} style={{ fontFamily: 'CustomFont', marginInlineEnd: 4 }}>{s.text}</Tag>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 130,
      align: 'center',
      render: (_, record) => {
        const isCanceled = isCanceledInvoice(record);
        const menuItems = [
          {
            key: 'view',
            label: <span style={{ fontFamily: 'CustomFont' }}>مشاهده</span>,
            icon: <EyeOutlined />,
            onClick: () =>
              navigateToDetail(`/finance/invoices/${record.id}`, {}, {
                listPath: '/finance/invoices',
                listLabel: 'فاکتورها',
                entityLabel: record.invoice_number || `فاکتور #${record.id}`,
              }),
          },
          {
            key: 'download',
            label: <span style={{ fontFamily: 'CustomFont' }}>دانلود</span>,
            icon: <DownloadOutlined />,
            disabled: isActionLoading(record.id, 'download'),
            onClick: () => handleDownloadInvoice(record.id),
          },
          ...(!isCanceled && canEditRecord(record)
            ? [
                {
                  key: 'edit',
                  label: <span style={{ fontFamily: 'CustomFont' }}>ویرایش</span>,
                  icon: <EditOutlined />,
                  onClick: () => navigateToDetail(`/finance/invoices/${record.id}/edit`),
                },
              ]
            : []),
          ...(!isCanceled
            ? [
                {
                  key: 'send',
                  label: <span style={{ fontFamily: 'CustomFont' }}>ارسال فاکتور</span>,
                  icon: <MailOutlined />,
                  disabled: isActionLoading(record.id, 'send'),
                  onClick: () => handleSendInvoice(record.id),
                },
                {
                  key: 'remind',
                  label: <span style={{ fontFamily: 'CustomFont' }}>یادآوری پرداخت</span>,
                  icon: <BellOutlined />,
                  disabled: isActionLoading(record.id, 'remind'),
                  onClick: () => handleReminder(record.id),
                },
                {
                  key: 'cancel',
                  label: <span style={{ fontFamily: 'CustomFont' }}>لغو فاکتور</span>,
                  icon: <CloseCircleOutlined />,
                  danger: true,
                  disabled: isActionLoading(record.id, 'cancel'),
                  onClick: () => handleCancelInvoice(record.id),
                },
              ]
            : []),
          ...(canDeleteRecord(record)
            ? [
                { type: 'divider' },
                {
                  key: 'delete',
                  label: <span style={{ fontFamily: 'CustomFont' }}>حذف</span>,
                  icon: <DeleteOutlined />,
                  danger: true,
                  disabled: isActionLoading(record.id, 'delete'),
                  onClick: () => confirmDeleteInvoice(record),
                },
              ]
            : []),
        ];

        return (
          <div className="w-full flex justify-center">
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} loading={actionLoadingKey?.startsWith(`${record.id}-`)} style={{ fontFamily: 'CustomFont' }}>
                عملیات
              </Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">فاکتورها</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">مدیریت فاکتورهای فروش</p>
              </div>
            </div>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <div className="flex flex-wrap gap-2 items-end mb-4">
              <div className="min-w-[260px]">
                <label className="block text-xs mb-1">بازه تاریخ</label>
                <PersianDatePicker
                  range
                  value={dateRangeValue}
                  onChange={handleDateRangeChange}
                  placeholder="از تاریخ — تا تاریخ"
                  style={{ fontFamily: 'CustomFont', width: '100%' }}
                />
              </div>
              <div>
                <label className="block text-xs mb-1">وضعیت</label>
                <Select value={filters.status} style={{ width: 170 }} onChange={(v) => setFilters((p) => ({ ...p, status: v, page: 1 }))}>
                  {statusOptions.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                </Select>
              </div>
              <div>
                <label className="block text-xs mb-1">مشتری</label>
                <ClientSelect
                  value={filters.client_id}
                  style={{ width: 200 }}
                  includeInactive
                  allowClear={false}
                  leadingOptions={[{ value: 'all', label: 'همه' }]}
                  onChange={(v) => setFilters((p) => ({ ...p, client_id: v ?? 'all', page: 1 }))}
                />
              </div>
              <Button icon={<ReloadOutlined />} onClick={() => fetchInvoices(filtersRef.current)} loading={loading}>بروزرسانی</Button>
              {canAdd ? (
                <div className="mr-auto flex-shrink-0">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    className="bg-green-600 hover:bg-green-700 border-0"
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => navigate('/finance/invoices/add')}
                  >
                    افزودن فاکتور
                  </Button>
                </div>
              ) : null}
            </div>
            {loading ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Table
                columns={columns}
                dataSource={invoices}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={{
                  current: pagination.current_page,
                  pageSize: pagination.per_page,
                  total: pagination.total,
                  onChange: (page, perPage) => setFilters((p) => ({ ...p, page, per_page: perPage })),
                  showTotal: (t) => <span style={{ fontFamily: 'CustomFont' }}>مجموع: {new Intl.NumberFormat('fa-IR').format(t)} مورد</span>,
                }}
                locale={{
                  emptyText: <Empty description={<span style={{ fontFamily: 'CustomFont' }}>فاکتوری یافت نشد</span>} />,
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Invoices;
